import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable, HpiFilterSelect, HpiStatsBar } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate, formatDateRange } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface EmployeeSummaryRow {
  user_id: number | null
  name: string
  pay_rate: number | null
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  pto_hours: number
  sto_hours: number
}

interface TimeEntry {
  id: number
  date: string | null
  employee: string | null
  employee_id: number | null
  case_name: string | null
  case_id: number | null
  otj_minutes: number
  travel_minutes: number
  total_minutes: number
  mileage: number
  mileage_rate: number | null
  vehicle_allowance: string | null
}

interface Expense {
  id: number
  date: string | null
  employee: string | null
  employee_id: number | null
  case_name: string | null
  case_id: number | null
  category: string | null
  description: string | null
  paid_to: string | null
  amount: number
}

interface PtoRecord {
  id: number
  start_date: string | null
  end_date: string | null
  request_type: number
  hours: number | null
  status: number
}

interface Employee {
  id: number
  name: string
}

interface SelectedEmployee {
  id: number
  name: string
  pay_rate: number | null
}

interface Props {
  employee_summaries: EmployeeSummaryRow[]
  time_entries: TimeEntry[]
  expenses: Expense[]
  pto_records: PtoRecord[]
  start_date: string
  end_date: string
  employees: Employee[]
  selected_employee: SelectedEmployee | null
  selected_employee_id: number | null
}

/* ── Helpers ────────────────────────────────────────────── */

function formatMinutes(minutes: number): string {
  if (minutes === 0) return "-"
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${String(m).padStart(2, "0")}m`
  if (m === 0) return `${h}h`
  return `${h}h ${String(m).padStart(2, "0")}m`
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatPtoDays(hours: number): string {
  if (!hours) return "-"
  return String(Math.floor(hours / 8))
}

function formatStoHours(hours: number): string {
  if (!hours) return "-"
  return String(hours)
}

/* ── Columns ───────────────────────────────────────────── */

function buildSummaryColumns(
  showEmployee: boolean,
  startDate: string,
): HpiColumn<EmployeeSummaryRow>[] {
  const cols: HpiColumn<EmployeeSummaryRow>[] = []

  if (showEmployee) {
    cols.push({
      header: "Name",
      width: "max-w-[200px]",
      render: (row) => (
        <div className="text-sm font-medium">
          {row.user_id ? (
            <a
              href={`/accounting/payroll/week_detail?employee_id=${row.user_id}&start_date=${startDate}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {row.name}
            </a>
          ) : (
            row.name
          )}
        </div>
      ),
    })
  }

  cols.push(
    {
      header: "OTJ",
      width: "max-w-[80px]",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.otj_minutes)}
        </div>
      ),
    },
    {
      header: "Travel",
      width: "max-w-[80px]",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.travel_minutes)}
        </div>
      ),
    },
    {
      header: "Total Hours",
      width: "max-w-[90px]",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.otj_minutes + row.travel_minutes)}
        </div>
      ),
    },
    {
      header: "Reg",
      width: "max-w-[80px]",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.regular_minutes)}
        </div>
      ),
    },
    {
      header: "OT",
      width: "max-w-[80px]",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.overtime_minutes)}
        </div>
      ),
    },
    {
      header: "PTO",
      subHeader: "days",
      width: "max-w-[60px]",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">{formatPtoDays(row.pto_hours)}</div>
      ),
    },
    {
      header: "STO",
      subHeader: "hours",
      width: "max-w-[60px]",
      align: "right",
      render: (row) => (
        <div className="text-right text-sm">
          {formatStoHours(row.sto_hours)}
        </div>
      ),
    },
  )

  return cols
}

function buildTimeColumns(showEmployee: boolean): HpiColumn<TimeEntry>[] {
  const cols: HpiColumn<TimeEntry>[] = [
    {
      header: "Date",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-sm">
          {row.case_id ? (
            <a
              href={`/cases/${row.case_id}/time_entries/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {formatDate(row.date, { month: "short", day: "numeric" })}
            </a>
          ) : (
            formatDate(row.date, { month: "short", day: "numeric" })
          )}
        </div>
      ),
    },
  ]
  if (showEmployee) {
    cols.push({
      header: "Employee",
      render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
    })
  }
  cols.push(
    {
      header: "Case",
      render: (row) => (
        <div className="text-sm">
          {row.case_id ? (
            <a
              href={`/cases/${row.case_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {row.case_name ?? "-"}
            </a>
          ) : (
            (row.case_name ?? "-")
          )}
        </div>
      ),
    },
    {
      header: "OTJ",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.otj_minutes)}
        </div>
      ),
    },
    {
      header: "Travel",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.travel_minutes)}
        </div>
      ),
    },
    {
      header: "Total",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.total_minutes)}
        </div>
      ),
    },
  )
  return cols
}

function buildMileageColumns(showEmployee: boolean): HpiColumn<TimeEntry>[] {
  const cols: HpiColumn<TimeEntry>[] = [
    {
      header: "Date",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-sm">
          {row.case_id ? (
            <a
              href={`/cases/${row.case_id}/time_entries/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {formatDate(row.date, { month: "short", day: "numeric" })}
            </a>
          ) : (
            formatDate(row.date, { month: "short", day: "numeric" })
          )}
        </div>
      ),
    },
  ]
  if (showEmployee) {
    cols.push({
      header: "Employee",
      render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
    })
  }
  cols.push(
    {
      header: "Case",
      render: (row) => (
        <div className="text-sm">
          {row.case_id ? (
            <a
              href={`/cases/${row.case_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {row.case_name ?? "-"}
            </a>
          ) : (
            (row.case_name ?? "-")
          )}
          {row.mileage_rate != null && row.mileage_rate > 0 && (
            <span className="text-hpi-med-gray ml-1 text-xs">
              @ {formatCurrency(row.mileage_rate)}/mi
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Miles",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">
          {row.mileage > 0 ? row.mileage : "-"}
        </div>
      ),
    },
  )
  return cols
}

function buildExpenseColumns(showEmployee: boolean): HpiColumn<Expense>[] {
  const cols: HpiColumn<Expense>[] = [
    {
      header: "Date",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-sm">
          {row.case_id ? (
            <a
              href={`/cases/${row.case_id}/expenses/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {formatDate(row.date, { month: "short", day: "numeric" })}
            </a>
          ) : (
            formatDate(row.date, { month: "short", day: "numeric" })
          )}
        </div>
      ),
    },
  ]
  if (showEmployee) {
    cols.push({
      header: "Employee",
      render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
    })
  }
  cols.push(
    {
      header: "Description",
      render: (row) => {
        const parts: string[] = []
        if (row.category) parts.push(row.category)
        if (row.description) parts.push(row.description)
        if (row.paid_to) parts.push(`Paid to: ${row.paid_to}`)
        return (
          <div className="max-w-[300px] truncate text-sm">
            {parts.join(" - ") || "-"}
          </div>
        )
      },
    },
    {
      header: "Amount",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-right text-sm font-medium">
          {formatCurrency(row.amount)}
        </div>
      ),
    },
  )
  return cols
}

function buildPtoColumns(): HpiColumn<PtoRecord>[] {
  return [
    {
      header: "Start",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-sm">
          {row.id ? (
            <a
              href={`/pto_requests/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {formatDate(row.start_date, { month: "short", day: "numeric" })}
            </a>
          ) : (
            formatDate(row.start_date, { month: "short", day: "numeric" })
          )}
        </div>
      ),
    },
    {
      header: "End",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-sm">
          {formatDate(row.end_date, { month: "short", day: "numeric" })}
        </div>
      ),
    },
    {
      header: "Type",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-sm">
          {row.request_type === 0
            ? "Vacation"
            : row.request_type === 1
              ? "Sick"
              : "Other"}
        </div>
      ),
    },
    {
      header: "Hours",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">{row.hours?.toFixed(1) ?? "-"}</div>
      ),
    },
  ]
}

/* ── Summary Totals Row ──────────────────────────────── */

function SummaryTotals({
  summaries,
  showEmployee,
}: {
  summaries: EmployeeSummaryRow[]
  showEmployee: boolean
}) {
  const totals = summaries.reduce(
    (acc, s) => ({
      otj_minutes: acc.otj_minutes + s.otj_minutes,
      travel_minutes: acc.travel_minutes + s.travel_minutes,
      regular_minutes: acc.regular_minutes + s.regular_minutes,
      overtime_minutes: acc.overtime_minutes + s.overtime_minutes,
      pto_hours: acc.pto_hours + s.pto_hours,
      sto_hours: acc.sto_hours + s.sto_hours,
    }),
    {
      otj_minutes: 0,
      travel_minutes: 0,
      regular_minutes: 0,
      overtime_minutes: 0,
      pto_hours: 0,
      sto_hours: 0,
    },
  )

  return (
    <div className="border-hpi-dark-gray/30 flex items-start gap-3 border-t-2 bg-white px-4 py-3">
      {showEmployee && (
        <div className="max-w-[200px] min-w-0 flex-1">
          <div className="text-sm font-bold">Total</div>
        </div>
      )}
      <div className="max-w-[80px] min-w-0 flex-1 text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.otj_minutes)}
        </div>
      </div>
      <div className="max-w-[80px] min-w-0 flex-1 text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.travel_minutes)}
        </div>
      </div>
      <div className="max-w-[90px] min-w-0 flex-1 text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.otj_minutes + totals.travel_minutes)}
        </div>
      </div>
      <div className="max-w-[80px] min-w-0 flex-1 text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.regular_minutes)}
        </div>
      </div>
      <div className="max-w-[80px] min-w-0 flex-1 text-right">
        <div className="text-sm font-bold">
          {formatMinutes(totals.overtime_minutes)}
        </div>
      </div>
      <div className="max-w-[60px] min-w-0 flex-1 text-right">
        <div className="text-sm font-bold">
          {formatPtoDays(totals.pto_hours)}
        </div>
      </div>
      <div className="max-w-[60px] min-w-0 flex-1 text-right">
        <div className="text-sm font-bold">
          {formatStoHours(totals.sto_hours)}
        </div>
      </div>
    </div>
  )
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Payroll History", href: "/accounting/payroll/history" },
  { title: "Week Detail", href: "#" },
]

export default function WeekDetail({
  employee_summaries,
  time_entries,
  expenses,
  pto_records,
  start_date,
  end_date,
  employees,
  selected_employee,
  selected_employee_id,
}: Props) {
  const [employeeId, setEmployeeId] = useState<string>(
    selected_employee_id?.toString() ?? "",
  )

  const showEmployee = !selected_employee_id
  const summaryColumns = buildSummaryColumns(showEmployee, start_date)
  const timeColumns = buildTimeColumns(showEmployee)
  const mileageColumns = buildMileageColumns(showEmployee)
  const expenseColumns = buildExpenseColumns(showEmployee)
  const ptoColumns = buildPtoColumns()

  // Split time entries into work vs mileage-only
  const workEntries = time_entries.filter(
    (t) => t.otj_minutes > 0 || t.travel_minutes > 0,
  )
  const mileageEntries = time_entries.filter(
    (t) => t.vehicle_allowance === "Yes" && t.mileage > 0,
  )

  const handleFilterChange = (value: string) => {
    setEmployeeId(value)
    const params: Record<string, string> = { start_date }
    if (value) params.employee_id = value
    router.get("/accounting/payroll/week_detail", params, {
      preserveState: true,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Week Detail - ${formatDateRange(start_date, end_date)}`} />

      <div className="p-6">
        {/* Header */}
        <HpiStatsBar
          stats={[
            ...(selected_employee
              ? [
                  { label: "Employee", value: selected_employee.name },
                  {
                    label: "Pay Rate",
                    subLabel: "/hr",
                    value: selected_employee.pay_rate
                      ? formatCurrency(selected_employee.pay_rate)
                      : "-",
                  },
                ]
              : []),
            { label: "Period", value: formatDateRange(start_date, end_date) },
            {
              label: "Employees",
              subLabel: "Active",
              value: employee_summaries.length,
            },
          ]}
          className="mb-6"
        />

        {/* Filter + Back */}
        <div className="border-hpi-light-gray mb-6 flex items-end gap-3 rounded-lg border bg-white px-4 py-3">
          <HpiFilterSelect
            label="Employee"
            value={employeeId}
            onChange={handleFilterChange}
            options={employees.map((e) => ({ value: e.id, label: e.name }))}
            placeholder="All Employees"
          />
          <Link
            href={`/accounting/payroll/history?start_date=${start_date}&end_date=${end_date}`}
            className="bg-hpi-dark-blue rounded px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Back to History
          </Link>
        </div>

        {/* Employee Summary */}
        {employee_summaries.length > 0 && (
          <HpiDataTable<EmployeeSummaryRow>
            title="Summary by Employee"
            columns={summaryColumns}
            data={employee_summaries}
            keyFn={(row) => row.user_id ?? "total"}
            className="mb-6"
            pagination={
              employee_summaries.length > 1 ? (
                <SummaryTotals
                  summaries={employee_summaries}
                  showEmployee={showEmployee}
                />
              ) : undefined
            }
          />
        )}

        {/* Time Entries */}
        {workEntries.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<TimeEntry>
              title={`Time Entries (${workEntries.length})`}
              columns={timeColumns}
              data={workEntries}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {/* Mileage */}
        {mileageEntries.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<TimeEntry>
              title={`Mileage (${mileageEntries.length})`}
              columns={mileageColumns}
              data={mileageEntries}
              keyFn={(row) => `mi-${row.id}`}
            />
          </div>
        )}

        {/* Expenses */}
        {expenses.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<Expense>
              title={`Expenses (${expenses.length})`}
              columns={expenseColumns}
              data={expenses}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {/* PTO */}
        {pto_records.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PtoRecord>
              title={`PTO (${pto_records.length})`}
              columns={ptoColumns}
              data={pto_records}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {employee_summaries.length === 0 &&
          workEntries.length === 0 &&
          expenses.length === 0 &&
          pto_records.length === 0 && (
            <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
              No payroll data for this period.
            </div>
          )}
      </div>
    </AppLayout>
  )
}
